/* eslint-disable import/prefer-default-export */
import humps from 'humps';
import { AppDispatch } from 'redux/store';
import { Cadence } from 'redux/schemas/models/mentoring-program-sessions';
import PusherService from 'shared/services/pusher-service';
import moment from 'moment';
import t from 'react-translate';
import { getISOString } from 'athena/components/mentoring-program/home/participant/session-edit-mode/session-edit-mode';
import {
  MentorshipProgramProfileQuestion,
  ProgramLevelQuestion,
} from 'redux/schemas/models/mentoring-program-profile-questions';
import { Event, EventList } from './event-types';
import momentTimezone from 'moment-timezone';
import { SESSION_DATE_FORMAT, TIME_FORMAT } from 'athena/components/constants';
import { MigratedFields } from 'redux/schemas/models/account-fields';

export const configureChannel = (
  channelName: string,
  events: EventList,
  dispatch: AppDispatch,
) => {
  const pusherChannel = PusherService.setupChannel(channelName);

  if (!pusherChannel) {
    return () => {};
  }

  const boundHandlers = events.map(({ name, handler }) => {
    const wrappedHandler = (event: Event) => {
      const camelizedEvent = humps.camelizeKeys(event) as Event;
      handler(camelizedEvent, dispatch);
    };
    pusherChannel.bind(name, wrappedHandler);
    return { name, wrappedHandler };
  });

  return () => {
    boundHandlers.forEach(({ name, wrappedHandler }) => pusherChannel.unbind(name, wrappedHandler));
    PusherService.removeChannel(channelName);
  };
};

export const getDayOfWeekAndNthWeek = (startDate: string) => {
  const start = moment(startDate);
  const dayOfWeek = start.format('dddd');
  const dayOfMonth = start.date();
  const daysInMonth = start.daysInMonth();

  let nthWeek: string;
  if (dayOfMonth <= 7) {
    nthWeek = 'FIRST';
  } else if (dayOfMonth <= 14) {
    nthWeek = 'SECOND';
  } else if (dayOfMonth <= 21) {
    nthWeek = 'THIRD';
  } else if (dayOfMonth <= daysInMonth - 7) {
    nthWeek = 'FOURTH';
  } else {
    nthWeek = 'LAST';
  }

  return { dayOfWeek, nthWeek };
};

const getProgramText = ({
  nthWeek,
  endDate,
  closureDate,
  hasClosureDate,
  translations,
}: {
  nthWeek: string,
  endDate: string,
  closureDate: string,
  hasClosureDate: boolean,
  translations: any,
}) => {
  const shouldShowClosureText = hasClosureDate && getISOString(closureDate) === getISOString(endDate);
  const programClosureText = shouldShowClosureText ? translations.PROGRAM_CLOSURE_DATE() : '';
  const nthWeekText = translations.NTH_WEEK[nthWeek]();
  let endDateText = '';

  if (endDate) {
    endDateText = moment(endDate).format('MMMM D, YYYY');
  } else if (closureDate) {
    endDateText = moment(closureDate).format('MMMM D, YYYY');
  }
  return { endDateText, programClosureText, nthWeekText };
};

const getCadenceText = ({
  cadence,
  translations,
  programClosureText,
  dayOfWeek,
  endDateText,
  nthWeekText,
  endDateChanged,
}: {
  cadence: Cadence,
  translations: any,
  programClosureText: string,
  dayOfWeek: string,
  endDateText: string,
  nthWeekText: string,
  endDateChanged?: boolean,
}): string => {
  if (!cadence) {
    return '';
  }
  switch (cadence) {
    case Cadence.WEEKLY:
      return endDateChanged
        ? translations.WEEKLY(programClosureText, dayOfWeek, endDateText)
        : translations.WEEKLY_NO_END_DATE(dayOfWeek);
    case Cadence.BI_WEEKLY:
      return endDateChanged
        ? translations.BI_WEEKLY(programClosureText, dayOfWeek, endDateText)
        : translations.BI_WEEKLY_NO_END_DATE(dayOfWeek);
    case Cadence.MONTHLY:
      return endDateChanged
        ? translations.MONTHLY(programClosureText, dayOfWeek, endDateText, nthWeekText)
        : translations.MONTHLY_NO_END_DATE(dayOfWeek, nthWeekText);
    default:
      return translations.DEFAULT(dayOfWeek, endDateText || programClosureText);
  }
};

type PreviewTextParams = {
  cadence: Cadence;
  startDate: string;
  endDate?: string;
  closureDate?: string;
};

export const getPreviewText = ({
  cadence,
  startDate,
  endDate,
  closureDate,
}: PreviewTextParams): string => {
  const { dayOfWeek, nthWeek } = getDayOfWeekAndNthWeek(startDate);
  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.PREVIEW_TEXT;
  const newTranslations = translations.NEW;

  const {
    endDateText,
    programClosureText,
    nthWeekText,
  } = getProgramText({
    nthWeek,
    endDate,
    closureDate,
    hasClosureDate: !!closureDate,
    translations,
  });

  return getCadenceText({
    cadence,
    translations: newTranslations,
    programClosureText,
    dayOfWeek,
    endDateText,
    nthWeekText,
    endDateChanged: true,
  });
};

type RecurrenceUpdateParams = {
  cadence?: Cadence;
  startDate: string;
  endDate?: string;
  closureDate?: string;
  hasClosureDate?: boolean;
  cadenceChanged?: boolean;
  endDateChanged?: boolean;
};

export const getRecurrenceUpdatePreview = ({
  cadence,
  startDate,
  endDate,
  closureDate,
  hasClosureDate,
  cadenceChanged,
  endDateChanged,
}: RecurrenceUpdateParams): string => {
  const { dayOfWeek, nthWeek } = getDayOfWeekAndNthWeek(startDate);
  const translations = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.EDIT_MODE.PREVIEW_TEXT;
  const editTranslations = translations.EDIT;

  const {
    endDateText,
    programClosureText,
    nthWeekText,
  } = getProgramText({
    nthWeek,
    endDate,
    closureDate,
    hasClosureDate,
    translations,
  });

  if (endDateChanged && !cadenceChanged) {
    return editTranslations.ONLY_END_DATE(endDateText);
  }

  if (cadenceChanged) {
    return getCadenceText({
      cadence,
      translations: editTranslations,
      programClosureText,
      dayOfWeek,
      endDateText,
      nthWeekText,
      endDateChanged,
    });
  }

  return '';
};

const getQuestionList = (question: MentorshipProgramProfileQuestion) => {
  const { profileQuestion } = question;
  const { questionList } = (profileQuestion as ProgramLevelQuestion) || {};
  return questionList;
};

export const sortQuestionByQuestionIndex = (
  firstQuestion: MentorshipProgramProfileQuestion,
  secondQuestion: MentorshipProgramProfileQuestion,
) => {
  const { questionIndex: firstQuestionIndex } = getQuestionList(firstQuestion) || {};
  const { questionIndex: secondQuestionIndex } = getQuestionList(secondQuestion) || {};
  return firstQuestionIndex - secondQuestionIndex;
};

export const getUniqueKeyForTimeSlots = ({
  mentorshipProgramConnectionId,
  mentorshipProgramId,
  meetingDuration,
  meetingDays,
}) => (
  `${mentorshipProgramConnectionId}-${mentorshipProgramId}-${meetingDuration}-${meetingDays[0]}`
);

export const formatSessionTime = (dateString, duration, timeZone) => {
  const { ianaTimeZone } = timeZone;
  try {
    const startTime = momentTimezone.tz(dateString, ianaTimeZone);
    const endTime = duration ? startTime.clone().add(duration, 'minutes') : null;
    const formattedStartTime = startTime.format(`${SESSION_DATE_FORMAT} • ${TIME_FORMAT}`);
    const formattedEndTime = endTime ? ` - ${endTime.format(TIME_FORMAT)}` : '';
    return `${formattedStartTime}${formattedEndTime}`;
  } catch (error) {
    return '';
  }
};

export const getDisplayNameForMigratedField = (migratedField: MigratedFields) => {
  switch (migratedField) {
    case MigratedFields.WORK:
      return t.ORG_LEVEL_PROFILE.ORG_FIELDS_DEFAULT_TEXTS.WORK();
    case MigratedFields.EDUCATION:
      return t.ORG_LEVEL_PROFILE.ORG_FIELDS_DEFAULT_TEXTS.EDUCATION();
    case MigratedFields.JOB_TITLE:
      return t.ORG_LEVEL_PROFILE.ORG_FIELDS_DEFAULT_TEXTS.JOB_TITLE();
    default:
      return '';
  }
};
